export type VoyageEvaluationView = "landing" | "performance";

// Preserves the Power BI summary projection and its nullable Snowflake values.
export interface VoyageEvaluationSummary {
  deliveryDate: string | null;
  commenceDate: string | null;
  completeDate: string | null;
  imo: string;
  vesselName: string;
  voyage: string;
  vesselType: string | null;
  ladenComment: string | null;
  ballastComment: string | null;
  evaluationDate: string | null;
  contractType: string | null;
  oprType: string | null;
  tcStatus: string | null;
  voyageStatus: string | null;
  evaluated: boolean;
  ladenSpeed: number | null;
  ladenConsumption: number | null;
  ladenDraft: number | null;
  ladenAuxEngine: number | null;
  znLink: string | null;
  imosLink: string | null;
  ballastSpeed: number | null;
  ballastConsumption: number | null;
  ballastDraft: number | null;
  ballastAuxEngine: number | null;
  cleanHullSpeedLadenEco: number | null;
  cleanHullSpeedBallastEco: number | null;
  cleanHullSpeedLadenFull: number | null;
  cleanHullSpeedBallastFull: number | null;
  totalConsumptionLadenEco: number | null;
  totalConsumptionBallastEco: number | null;
  totalConsumptionLadenFull: number | null;
  totalConsumptionBallastFull: number | null;
  currentPerformanceSpeedLadenEco: number | null;
  currentPerformanceSpeedBallastEco: number | null;
  currentPerformanceSpeedLadenFull: number | null;
  currentPerformanceSpeedBallastFull: number | null;
  performanceEvaluationDate: string | null;
}

export interface VoyageEvaluationDashboard {
  voyages: VoyageEvaluationSummary[];
  total: number;
  offset: number;
  limit: number;
  filterOptions: VoyageEvaluationFilterOptions;
  lastRefresh: string;
}

export interface VoyageEvaluationFilterOptions {
  imos: string[];
  vesselNames: string[];
  voyages: string[];
  vesselTypes: string[];
  contractTypes: string[];
  oprTypes: string[];
  tcStatuses: string[];
  voyageStatuses: string[];
}

export interface VoyageEvaluationLandingFilters {
  imo: string;
  vesselName: string;
  voyage: string;
  vesselType: string;
  contractType: string;
  oprType: string;
  tcStatus: string;
  voyageStatus: string;
  completeDateFrom: string;
  completeDateTo: string;
  evaluated: string;
}

export const EMPTY_VOYAGE_EVALUATION_LANDING_FILTERS: VoyageEvaluationLandingFilters = {
  imo: "",
  vesselName: "",
  voyage: "",
  vesselType: "",
  contractType: "",
  oprType: "",
  tcStatus: "",
  voyageStatus: "",
  completeDateFrom: "",
  completeDateTo: "",
  evaluated: "",
};

export interface VoyagePerformanceReport {
  formGuid: string;
  formLink: string | null;
  znLink: string | null;
  imo: string;
  vesselCode: string | null;
  vesselName: string;
  voyage: string;
  reportDate: string;
  reportType: string | null;
  isZnEnriched: boolean;
  location: string | null;
  condition: string | null;
  port: string | null;
  steamingHours: number | null;
  sog: number | null;
  observedDistance: number | null;
  reportedStw: number | null;
  calculatedStw: number | null;
  draftCorrectedSpeed: number | null;
  mainEngineRpm: number | null;
  mainEngineOutputPercent: number | null;
  sfoc: number | null;
  slipPercent: number | null;
  draft: number | null;
  totalMeConsumption24h: number | null;
  totalAuxConsumption24h: number | null;
  totalConsumption24h: number | null;
  normalisedConsumption24h: number | null;
  primaryWaveHeight: number | null;
  windSeaWaveHeight: number | null;
  windBeaufort: number | null;
  currentFactor: number | null;
  noonReportWindForce: number | null;
  noonReportSwell: number | null;
  goodWeather: boolean | null;
  evaluatedSpeed: number | null;
  evaluatedConsumption: number | null;
  evaluatedDraft: number | null;
  evaluatedAuxEngine: number | null;
}

export interface VoyagePerformanceReportsResponse {
  reports: VoyagePerformanceReport[];
  total: number;
  offset: number;
  limit: number;
}

export interface VoyagePerformanceFilters {
  condition: string;
  goodWeather: string;
  isZnEnriched: string;
  steamingHoursMin: string;
  steamingHoursMax: string;
  currentFactorMin: string;
  currentFactorMax: string;
  draftMin: string;
  draftMax: string;
  windBeaufortMin: string;
  windBeaufortMax: string;
  noonReportWindForceMin: string;
  noonReportWindForceMax: string;
  noonReportSwellMin: string;
  noonReportSwellMax: string;
  combinedWaveHeightMetersMin: string;
  combinedWaveHeightMetersMax: string;
  combinedWaveHeightDsMin: string;
  combinedWaveHeightDsMax: string;
}

// Mirrors the recovered Power BI bookmark defaults.
export const DEFAULT_VOYAGE_PERFORMANCE_FILTERS: VoyagePerformanceFilters = {
  condition: "Laden",
  goodWeather: "",
  isZnEnriched: "",
  steamingHoursMin: "20",
  steamingHoursMax: "",
  currentFactorMin: "-1",
  currentFactorMax: "1",
  draftMin: "4.8",
  draftMax: "17.8",
  windBeaufortMin: "1",
  windBeaufortMax: "7",
  noonReportWindForceMin: "",
  noonReportWindForceMax: "",
  noonReportSwellMin: "",
  noonReportSwellMax: "",
  combinedWaveHeightMetersMin: "0",
  combinedWaveHeightMetersMax: "7",
  combinedWaveHeightDsMin: "",
  combinedWaveHeightDsMax: "2",
};

export const EMPTY_VOYAGE_PERFORMANCE_FILTERS: VoyagePerformanceFilters = {
  condition: "",
  goodWeather: "",
  isZnEnriched: "",
  steamingHoursMin: "",
  steamingHoursMax: "",
  currentFactorMin: "",
  currentFactorMax: "",
  draftMin: "",
  draftMax: "",
  windBeaufortMin: "",
  windBeaufortMax: "",
  noonReportWindForceMin: "",
  noonReportWindForceMax: "",
  noonReportSwellMin: "",
  noonReportSwellMax: "",
  combinedWaveHeightMetersMin: "",
  combinedWaveHeightMetersMax: "",
  combinedWaveHeightDsMin: "",
  combinedWaveHeightDsMax: "",
};

export interface VoyageEvaluationAccessToken {
  email?: string;
  sub?: string;
}
